import { createContext, useContext, useEffect, useId } from 'react';
import type { SidebarMode } from './sidebarMode';
import { SIDEBAR_STORAGE_KEY } from './sidebarStorage';

/** Состояние меню оболочки и его команды: для шапки (кнопка меню) и содержимого панели. */
export interface IAppShellApi {
    /** Раскладка меню: широкое окно, узкое (< 1200 px: рейл и временная панель) или телефон (Drawer) */
    sidebarMode: SidebarMode;
    /** Меню закреплено и занимает место в раскладке */
    sidebarPinned: boolean;
    /** Полное меню видно: закреплено, временной панелью поверх контента или в Drawer */
    sidebarOpen: boolean;
    /**
     * Действие кнопки меню в шапке: в широком окне — закрепить или открепить (временная панель закрепляется на месте),
     * в узком — показать или скрыть временную панель, на телефоне — открыть или закрыть Drawer.
     */
    toggleSidebar: () => void;
    /** Имя кнопки меню в шапке для текущего состояния (`aria-label`) */
    sidebarToggleLabel: string;
    /** Показать полное меню временной панелью, не закрепляя (поиск по меню, щелчок по иконке рейла) */
    requestSidebarOpen: () => void;
    /** Переход из меню: временная панель и Drawer закрываются */
    onSidebarNavigate: () => void;
    /** Держать временную панель открытой, пока открыто окно или меню из неё; `source` — кто держит */
    holdSidebar: (source: string, active: boolean) => void;
    /** Меню и шапка скрыты и не занимают места, пока их скрывает хоть один `source` (`useShellFocus`) */
    focusShell: (source: string, active: boolean) => void;
    /** Основа ключей localStorage меню (перед ней — `storageNamespace` из `UiProvider`) */
    storageKey: string;
}

const noop = () => undefined;

/** Вне оболочки (истории, тесты) меню развёрнуто и ничем не управляет. */
export const DEFAULT_APP_SHELL_API: IAppShellApi = {
    sidebarMode: 'wide',
    sidebarPinned: true,
    sidebarOpen: true,
    toggleSidebar: noop,
    sidebarToggleLabel: 'Свернуть боковое меню',
    requestSidebarOpen: noop,
    onSidebarNavigate: noop,
    holdSidebar: noop,
    focusShell: noop,
    storageKey: SIDEBAR_STORAGE_KEY,
};

export const AppShellContext = createContext<IAppShellApi>(DEFAULT_APP_SHELL_API);

export const useAppShell = () => useContext(AppShellContext);

/** Пока `active`, временная панель не закрывается уходом курсора, щелчком мимо и Escape (окно или меню из панели). */
export const useSidebarHold = (active: boolean) => {
    const { holdSidebar } = useAppShell();
    const source = useId();
    useEffect(() => {
        holdSidebar(source, active);
        return () => holdSidebar(source, false);
    }, [holdSidebar, source, active]);
};

/**
 * Пока `active`, меню и шапка оболочки уезжают за край и не занимают места, у `.app-shell` — `data-focus="true"`:
 * страница на время полного экрана `<html>` остаётся без них. Вне оболочки ничего не делает.
 */
export const useShellFocus = (active: boolean) => {
    const { focusShell } = useAppShell();
    const source = useId();
    useEffect(() => {
        focusShell(source, active);
        return () => focusShell(source, false);
    }, [focusShell, source, active]);
};
